import { Button, Heading } from '@/components/atoms';
import {
  AnalyticsChart,
  MetricCard,
  DataTable,
  AnalyticsEmptyState,
} from '@/components/molecules';
import type { ColumnDef } from '@tanstack/react-table';
import type { Sale, AnalyticsCustomers } from '@/types';

interface CustomersTabProps {
  showEmptyChart: boolean;
  showAnalyticsEmptyState: boolean;
  onRowClick: (sale: Sale) => void;
  salesData: Sale[];
  columns: ColumnDef<Sale>[];
  monthlySalesData: Array<{ month: string; sales: number }>;
  emptyChartData: Array<{ month: string; sales: number }>;
  chartConfig: {
    sales: {
      label: string;
      color: string;
    };
  };
  analyticsData?: AnalyticsCustomers;
}

export const CustomersTab = ({
  showEmptyChart,
  showAnalyticsEmptyState,
  onRowClick,
  salesData,
  columns,
  monthlySalesData,
  emptyChartData,
  chartConfig,
  analyticsData,
}: CustomersTabProps) => {
  return (
    <div className="space-y-6">
      {/* Customer Trend Chart and Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Trend Chart */}
        <div className="lg:col-span-2">
          <AnalyticsChart
            title=""
            amount=""
            showEmptyChart={showEmptyChart}
            chartData={monthlySalesData}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
            summaryMetrics={[
              {
                title: 'Total Customers',
                value: showEmptyChart
                  ? '0'
                  : `${analyticsData?.totalCustomers || '0'}`,
              },
              {
                title: 'Total Leads',
                value: showEmptyChart
                  ? '0'
                  : `${analyticsData?.totalLeads || '0'}`,
              },
            ]}
          />
        </div>

        {/* Customer Metrics */}
        <div className="space-y-4">
          <MetricCard
            title="New Customers"
            value="25"
            subtitle="This month"
            icon="trending"
            showEmpty={showEmptyChart}
          />
          <MetricCard
            title="Returning Customers"
            value="125"
            subtitle="This month"
            icon="trending"
            showEmpty={showEmptyChart}
          />
          <MetricCard
            title="Conversion Rate"
            value="85%"
            subtitle="This month"
            icon="trending"
            showEmpty={showEmptyChart}
          />
        </div>
      </div>

      {/* Recent Customer Activities Section */}
      <div className="space-y-4">
        <Heading size="lg">Recent Customer Activities</Heading>
        {showAnalyticsEmptyState ? (
          <AnalyticsEmptyState message="You don't have any Customer" />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={salesData}
              onRowClick={onRowClick}
            />
            <div className="flex justify-center mt-4">
              <Button variant="outline" className="text-gray-600">
                View All
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
